import { useForm, useStore } from "@tanstack/react-form";
import {
	createFileRoute,
	redirect,
	useLoaderData,
	useNavigate,
	useParams,
	useRouteContext,
} from "@tanstack/react-router";
import {
	fetchQuery,
	graphql,
	useLazyLoadQuery,
	useMutation,
} from "react-relay";
import { toast } from "sonner";
import z from "zod";
import { useOnboardingAnalytics } from "@/analytics/onboarding";
import { Heading } from "@/components/heading";
import { Text } from "@/components/text";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { environment } from "@/relay/environment";
import type { rankingEventsOnboardingUpdateMutation } from "./__generated__/rankingEventsOnboardingUpdateMutation.graphql";
import type { rankingLoaderQuery } from "./__generated__/rankingLoaderQuery.graphql";
import type { rankingOnboardingQuery } from "./__generated__/rankingOnboardingQuery.graphql";

export const Route = createFileRoute("/_logged/onboarding/$club/ranking")({
	component: RouteComponent,
	loader: async ({ params }) => {
		const data = await fetchQuery<rankingLoaderQuery>(
			environment,
			graphql`
        query rankingLoaderQuery($club: String!) {
          club(slug: $club) {
            leaderboardEnabled: configuration(key: "LEADERBOARD")
          }
        }
      `,
			{
				club: params.club,
			},
		).toPromise();

		if (
			data?.club.leaderboardEnabled !== "1" &&
			data?.club.leaderboardEnabled !== "ENABLED"
		) {
			throw redirect({
				to: "/onboarding/$club/planos",
				params: { club: params.club },
			});
		}

		return {};
	},
});

function RouteComponent() {
	const navigate = useNavigate({ from: Route.fullPath });
	useLoaderData({ from: Route.id });
	const { club: clubSlug } = useParams({ from: Route.id });
	const { club: onboardingClub } = useRouteContext({
		from: "/_logged/onboarding/$club",
	});
	const { capture, trackError } = useOnboardingAnalytics("ranking", clubSlug);

	const data = useLazyLoadQuery<rankingOnboardingQuery>(
		graphql`
      query rankingOnboardingQuery($club: String!) {
        club(slug: $club) {
          ranking {
            events {
              event
              name
              points
              enabled
            }
          }
        }
      }
    `,
		{
			club: clubSlug,
		},
	);

	const [updateRankingEvents] =
		useMutation<rankingEventsOnboardingUpdateMutation>(
			graphql`
      mutation rankingEventsOnboardingUpdateMutation($club: String!, $events: [UpdateRankingEventInput!]!) {
        rankingEventsUpdate(club: $club, events: $events) {
          events {
            event
            name
            points
            enabled
          }
        }
      }
    `,
		);

	const [updateClub] = useMutation(
		graphql`
      mutation rankingOnboardingUpdateMutation($club: ID!, $payload: UpdateClubInput!) {
        updateClub(id: $club, payload: $payload) {
          id
        }
      }
    `,
	);

	const form = useForm({
		defaultValues: {
			events: [...data.club.ranking.events]
				.sort((a, b) => a.name.localeCompare(b.name))
				.map((ev) => ({
					points: ev.points,
					enabled: ev.enabled,
					name: ev.name,
					id: ev.event,
				})),
		},
		onSubmit: ({ value }) => {
			return new Promise((resolve, reject) => {
				const events = value.events.map((ev) => ({
					event: ev.id,
					points: ev.points,
					enabled: ev.enabled,
				}));

				updateRankingEvents({
					variables: {
						club: clubSlug,
						events,
					},
					onCompleted: (response, errors) => {
						if (errors?.length) {
							toast.error(errors[0].message);
							reject(new Error(errors[0].message));
							return;
						}
						updateClub({
							variables: {
								club: onboardingClub?.id ?? "",
								payload: {
									configurations: [
										{
											key: "ONBOARDING_LAST_PAGE",
											value: "ranking",
										},
									],
								},
							},
							onCompleted: (_clubResponse, clubErrors) => {
								if (clubErrors?.length) {
									toast.error(clubErrors[0].message);
									reject(new Error(clubErrors[0].message));
									return;
								}
								capture("completed", {
									enabled_events_count: value.events.filter(
										(event) => event.enabled,
									).length,
									total_events_count: value.events.length,
								});
								toast.success(
									"Configurações do ranking atualizadas com sucesso!",
								);
								navigate({
									to: "/onboarding/$club/planos",
									params: { club: clubSlug },
								});

								resolve(response);
							},
							onError: (error) => {
								toast.error("Erro ao finalizar onboarding!");
								reject(error);
							},
						});
					},
					onError: (error) => {
						trackError("submission", { error_message: error.message });
						toast.error("Erro ao atualizar configurações do ranking!");
						reject(error);
					},
				});
			});
		},
		validators: {
			onChange: z.object({
				events: z.array(
					z.object({
						id: z.string(),
						name: z.string(),
						points: z.number().min(0, "Pontos não podem ser negativos"),
						enabled: z.boolean(),
					}),
				),
			}),
		},
	});

	const events = useStore(form.store, (state) => state.values.events);

	return (
		<form
			className="container mx-auto p-6 max-w-2xl flex flex-col"
			onSubmit={(e) => {
				e.preventDefault();
				e.stopPropagation();
				form.handleSubmit();
			}}
		>
			<Card className="p-6 gap-0">
				<div>
					<Heading>Regras do Jogo</Heading>

					<Text>Defina o peso das interações para o Ranking.</Text>
				</div>

				<div className="mt-6">
					<div className="grid grid-cols-2 gap-4 mb-4">
						<div className="font-semibold text-sm pl-11">Ação</div>
						<div className="font-semibold text-sm text-right">Pontos</div>
					</div>

					<div className="flex flex-col gap-3">
						{events.map((ev, index) => (
							<div key={ev.id} className="grid grid-cols-2 gap-4 items-center">
								<div className="text-sm">
									<div className="flex items-center justify-start w-fit gap-3">
										<form.Field name={`events[${index}].enabled`}>
											{(field) => (
												<Switch
													checked={field.state.value as boolean}
													onCheckedChange={(checked) =>
														field.handleChange(checked)
													}
												/>
											)}
										</form.Field>
										<span>{ev.name}</span>
									</div>
								</div>

								<div className="flex flex-row items-center justify-end">
									<form.Field name={`events[${index}].points`}>
										{(field) => (
											<Input
												type="number"
												value={field.state.value as number}
												className="max-w-42"
												onChange={(e) =>
													field.handleChange(parseInt(e.target.value, 10) || 0)
												}
												min={0}
												data-invalid={field.state.meta.errors?.length > 0}
											/>
										)}
									</form.Field>
								</div>
							</div>
						))}
					</div>
				</div>

				<Separator className="mt-6" />

				<form.Subscribe selector={(state) => [state.isSubmitting]}>
					{([isSubmitting]) => (
						<div className="py-6 pb-0 flex items-center justify-between gap-3">
							<Button
								type="button"
								variant="outline"
								onClick={() =>
									navigate({
										to: "/onboarding/$club/eventos",
										params: { club: clubSlug },
									})
								}
								disabled={isSubmitting}
							>
								Voltar
							</Button>
							<Button
								type="submit"
								className="flex items-center gap-2"
								disabled={isSubmitting}
							>
								Finalizar Setup
								{isSubmitting && <Spinner />}
							</Button>
						</div>
					)}
				</form.Subscribe>
			</Card>
		</form>
	);
}
